import { useMemo, useState } from 'react';
import {
  LEGAL_URLS,
  buildCampaignUrl,
  buildShortLinkUrl,
  isValidCampaignSlug,
  type CampaignLink,
} from '@chefty/shared-types';
import { adminApi } from '../services/admin';
import { useAsync } from '../hooks/useAsync';
import { Eyebrow, EmptyState, Loading, Panel, StatCard } from '../components/ui';
import { fmtDateTime, fmtInt } from '../lib/format';

/** Where people arrive from. Free text is allowed; these are the ones actually used. */
const SOURCES = ['tiktok', 'instagram', 'youtube', 'reddit', 'newsletter', 'friend'];
/** Which surface there: a bio link behaves nothing like a link under one video. */
const MEDIUMS = ['bio', 'post', 'story', 'video', 'comment', 'dm'];

/** A never-clicked link has no timestamp yet — show a dash, not "Invalid Date". */
const when = (iso: string | null): string => (iso ? fmtDateTime(iso) : '—');

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1400);
        } catch {
          // Clipboard blocked (no permission, insecure origin): the value is on
          // screen and selectable, so this is a convenience, not the only way out.
        }
      }}
      className="rounded-lg border border-cream-600/20 px-2.5 py-1 text-xs font-medium text-cream-300 transition-colors hover:border-cream-600/40 hover:text-cream-100"
      aria-label={`Copy ${label}`}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function Field({
  label,
  value,
  onChange,
  options,
  placeholder,
  required,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options?: string[];
  placeholder?: string;
  required?: boolean;
  hint?: string;
}) {
  const listId = options ? `opts-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined;
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[0.62rem] uppercase tracking-wider text-cream-400">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        list={listId}
        required={required}
        placeholder={placeholder}
        className="w-44 rounded-lg border border-cream-600/20 bg-ink-850 px-3 py-2 font-mono text-sm text-cream-100 outline-none focus:border-cream-400/40"
      />
      {options && (
        <datalist id={listId}>
          {options.map((o) => (
            <option key={o} value={o} />
          ))}
        </datalist>
      )}
      {hint && <span className="text-[0.66rem] text-cream-500">{hint}</span>}
    </label>
  );
}

/**
 * Campaign links: build one, paste the short form into a profile, watch the clicks.
 *
 * The preview is built with the same `buildCampaignUrl` the redirect uses, so what is
 * shown here is exactly where visitors land — the two cannot drift apart.
 */
export function LinksPage() {
  const { data, error, loading, refresh } = useAsync(() => adminApi.links(), []);
  const [slug, setSlug] = useState('');
  const [source, setSource] = useState('');
  const [medium, setMedium] = useState('');
  const [campaign, setCampaign] = useState('');
  const [content, setContent] = useState('');
  const [label, setLabel] = useState('');
  const [targetPath, setTargetPath] = useState('/');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const preview = useMemo(() => {
    if (!source || !medium) return null;
    return {
      short: buildShortLinkUrl(slug || 'your-link', LEGAL_URLS.site),
      target: buildCampaignUrl(
        {
          targetPath: targetPath || '/',
          utmSource: source,
          utmMedium: medium,
          utmCampaign: campaign || null,
          utmContent: content || null,
        },
        LEGAL_URLS.site,
      ),
    };
  }, [slug, source, medium, campaign, content, targetPath]);

  const slugOk = slug === '' || isValidCampaignSlug(slug);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      await adminApi.createLink({
        slug: slug.trim().toLowerCase(),
        label: label.trim() || undefined,
        targetPath: targetPath.trim() || '/',
        utmSource: source.trim(),
        utmMedium: medium.trim(),
        utmCampaign: campaign.trim() || undefined,
        utmContent: content.trim() || undefined,
      });
      setSlug('');
      setLabel('');
      setCampaign('');
      setContent('');
      refresh();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not create the link');
    } finally {
      setBusy(false);
    }
  }

  async function remove(link: CampaignLink) {
    // Deleting breaks the link wherever it is already pasted, and the clicks it
    // collected go with it — so make the operator name it back.
    if (
      !window.confirm(`Delete /go/${link.slug}? Anywhere it is already posted will stop working.`)
    )
      return;
    setBusy(true);
    try {
      await adminApi.deleteLink(link.id);
      refresh();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not delete the link');
    } finally {
      setBusy(false);
    }
  }

  const links = data?.links ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Eyebrow>Acquisition</Eyebrow>
        <h1 className="font-display text-2xl font-bold text-cream-100">Campaign links</h1>
        <p className="mt-1 max-w-3xl text-sm text-cream-400">
          Build a short link, paste it where people find you (TikTok bio, Instagram profile, a
          YouTube description). Each click is counted here — including visitors who decline
          analytics cookies and never appear in PostHog — and the visitor lands on the site with UTM
          tags, which is what PostHog groups its reports by.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Links" value={fmtInt(links.length)} accent="teal" />
        <StatCard label="Clicks (all time)" value={fmtInt(data?.totalClicks ?? 0)} />
      </div>

      <Panel>
        <form onSubmit={add} className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start gap-3">
            <Field
              label="Link name"
              value={slug}
              onChange={setSlug}
              required
              placeholder="tiktok-bio"
              hint={slugOk ? 'Appears in the URL' : 'Lowercase letters, digits and dashes'}
            />
            <Field
              label="Source"
              value={source}
              onChange={setSource}
              options={SOURCES}
              required
              placeholder="tiktok"
              hint="utm_source"
            />
            <Field
              label="Placement"
              value={medium}
              onChange={setMedium}
              options={MEDIUMS}
              required
              placeholder="bio"
              hint="utm_medium"
            />
            <Field
              label="Campaign"
              value={campaign}
              onChange={setCampaign}
              placeholder="launch"
              hint="utm_campaign, optional"
            />
            <Field
              label="Content"
              value={content}
              onChange={setContent}
              placeholder="coq-au-vin"
              hint="utm_content, optional"
            />
            <Field label="Note" value={label} onChange={setLabel} placeholder="TikTok bio" />
            <Field label="Lands on" value={targetPath} onChange={setTargetPath} placeholder="/" />
          </div>

          {preview && (
            <div className="rounded-xl border border-cream-600/15 bg-ink-850/60 p-4 text-sm">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-[0.62rem] uppercase tracking-wider text-cream-400">
                  Paste this
                </span>
                <code className="font-mono text-cream-100">{preview.short}</code>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <span className="text-[0.62rem] uppercase tracking-wider text-cream-400">
                  Sends to
                </span>
                <code className="break-all font-mono text-xs text-cream-400">{preview.target}</code>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={busy || !slugOk || !slug}
              className="rounded-lg bg-cream-100 px-4 py-2 text-sm font-semibold text-ink-900 disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Create link'}
            </button>
            {formError && <p className="text-sm text-bad">{formError}</p>}
          </div>
        </form>
      </Panel>

      <Panel pad={false}>
        {loading && !data ? (
          <div className="p-5">
            <Loading />
          </div>
        ) : error ? (
          <div className="p-5">
            <EmptyState title="Couldn’t load links" hint={error} />
          </div>
        ) : links.length === 0 ? (
          <div className="p-5">
            <EmptyState
              title="No links yet"
              hint="Create one above, then paste it into your TikTok or Instagram profile."
            />
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-cream-600/15 text-[0.62rem] uppercase tracking-wider text-cream-400">
                <th className="px-5 py-3 font-medium">Link</th>
                <th className="px-5 py-3 font-medium">Tags</th>
                <th className="px-5 py-3 text-right font-medium">Clicks</th>
                <th className="px-5 py-3 font-medium">Last click</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-600/10">
              {links.map((link) => (
                <tr key={link.id} className="align-top">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <code className="font-mono text-cream-100">/go/{link.slug}</code>
                      <CopyButton value={link.shortUrl} label={`link ${link.slug}`} />
                    </div>
                    {link.label && <div className="mt-1 text-xs text-cream-400">{link.label}</div>}
                  </td>
                  <td className="px-5 py-3 text-xs text-cream-400">
                    <div>
                      {link.utmSource} · {link.utmMedium}
                      {link.utmCampaign ? ` · ${link.utmCampaign}` : ''}
                      {link.utmContent ? ` · ${link.utmContent}` : ''}
                    </div>
                    <div className="mt-1 break-all text-cream-500">{link.targetUrl}</div>
                  </td>
                  <td className="tnum px-5 py-3 text-right font-semibold text-cream-100">
                    {fmtInt(link.clicks)}
                  </td>
                  <td className="px-5 py-3 text-xs text-cream-400">{when(link.lastClickAt)}</td>
                  <td className="px-5 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => remove(link)}
                      disabled={busy}
                      className="rounded-lg border border-cream-600/20 px-2.5 py-1 text-xs text-cream-400 transition-colors hover:border-bad/40 hover:text-bad disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <p className="text-xs text-cream-500">
        Clicks here count every tap, humans and (filtered) crawlers aside; PostHog counts only
        visitors who accepted analytics cookies. The two numbers will not match, and the gap between
        them is roughly the share of visitors who declined.
      </p>
    </div>
  );
}
